import { useQuery } from "@apollo/client";
import {
  GetPromotionAnalysis,
  GetPromotionAnalysisVariables,
} from "_graphql/visal/queries/__generated__/GetPromotionAnalysis";
import {
  GetPromotionClicksData,
  GetPromotionClicksDataVariables,
} from "_graphql/visal/queries/__generated__/GetPromotionClicksData";
import {
  getPromotionAnalytics,
  getPromotionClicks,
} from "_graphql/visal/queries/promotions";
import { TableLoader } from "components/tables";
import {
  FileText,
  Laptop2,
  List,
  MousePointer2,
  MousePointerSquare,
  Smartphone,
  Tablet,
} from "lucide-react";
import { useSearch } from "react-location";
import { LocationGenerics } from "router/location";

type Props = {};

const PromotionPerformanceView = (props: Props) => {
  const searchParams = useSearch<LocationGenerics>();

  const { loading, data, refetch } = useQuery<
    GetPromotionClicksData,
    GetPromotionClicksDataVariables
  >(getPromotionClicks, {
    variables: {
      promotionId: searchParams?.id || "",
    },
  });
  const {
    loading: loadingAnalysis,
    data: analysisData,
    refetch: refetchAnalysis,
  } = useQuery<GetPromotionAnalysis, GetPromotionAnalysisVariables>(
    getPromotionAnalytics,
    {
      variables: {
        promotionId: searchParams?.id || "",
      },
    }
  );

  const analyticsStat = [
    {
      id: 1,
      name: "Total Impressions",
      icon: MousePointerSquare,
      value: analysisData?.getPromotionAnalysis?.total_impressions || 0,
    },
    {
      id: 2,
      name: "Total Unique Clicks",
      icon: MousePointer2,
      value: analysisData?.getPromotionAnalysis?.total_unique_clicks || 0,
    },
  ];

  const deviceStat = [
    {
      id: 1,
      name: "Desktop Clicks",
      icon: Laptop2,
      value: analysisData?.getPromotionAnalysis?.unique_devices?.desktop || 0,
    },
    {
      id: 2,
      name: "Mobile Clicks",
      icon: Smartphone,
      value: analysisData?.getPromotionAnalysis?.unique_devices?.mobile || 0,
    },
    {
      id: 3,
      name: "Tablet Clicks",
      icon: Tablet,
      value: analysisData?.getPromotionAnalysis?.unique_devices?.tablet || 0,
    },
  ];

  if (loading || loadingAnalysis) {
    return <TableLoader items={[1, 1, 1, 1]} />;
  }

  return (
    <div className="flex flex-col flex-1 overflow-y-auto">
      <div className="flex justify-between items-center gap-5 px-2 sm:flex-row flex-col ">
        <div className="space-y-2 flex-1">
          {analyticsStat?.map((item) => (
            <div
              key={item.id}
              className="relative overflow-hidden rounded-lg bg-card-500 px-3 py-4 sm:px-6 sm:pt-6 shadow"
            >
              <dt>
                <div className="absolute rounded-md bg-primary-500 p-2 sm:p-3">
                  <item.icon
                    className="h-5 w-5 sm:h-6 sm:w-6 text-card-400"
                    aria-hidden="true"
                  />
                </div>
                <p className="ml-12 sm:ml-16 truncate text-xs sm:text-sm font-medium text-tertiary">
                  {item.name}
                </p>
              </dt>
              <dd className="ml-12 sm:ml-16 flex justify-between items-end pb-4 sm:pb-7">
                <p className="text-xl sm:text-2xl font-semibold text-gray-800">
                  {item.value}
                </p>
              </dd>
            </div>
          ))}
        </div>
        <div className="relative overflow-hidden rounded-lg bg-card-500 px-3 py-2 sm:px-6 sm:pt-7 shadow flex-1 space-y-4">
          {deviceStat?.map((item) => (
            <div
              key={item.id}
              className="w-full  flex gap-10 items-center justify-start "
            >
              <div className=" rounded-md bg-primary-500 p-2 sm:p-3">
                <item.icon
                  className="h-6 w-6 text-card-400"
                  aria-hidden="true"
                />
              </div>
              <div className="pb-2">
                <p className=" truncate text-xs sm:text-sm font-medium text-tertiary">
                  {item.name}
                </p>
                <p className="text-xl sm:text-2xl font-semibold text-primary">
                  {item.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="my-5 border-b border-black/10 py-2">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Promotion Click Information
        </h3>
        <h6 className="text-sm  text-gray-500 ">
          Details of each click on the promotion
        </h6>
      </div>
      {data?.getPromotionClicksData?.length === 0 ? (
        <div className="flex justify-center items-center h-96  w-full flex-col gap-5 text-gray-500">
          <FileText size={35} className="text-gray-300" />
          No clicks recorded for this promotion.
        </div>
      ) : (
        <div className="flex-1 flex-col mx-auto py-4 sm:py-6 overflow-scroll overflow-y-scroll light flex overflow-x-hidden w-full h-56 max-h-96 no-scrollbar">
          {data?.getPromotionClicksData?.map((click, index) => (
            <div
              key={`${click?.ip_address} - ${index}`}
              className="space-y-2 p-4 m-2 border rounded-lg  border-black/5 shadow "
            >
              <div className=" border-b  border-black/10 pb-1 mb-1">
                <span className="text-xs sm:text-sm font-medium text-tertiary">
                  Device :
                </span>
                <span className="ml-2 text-gray-500 font-medium">
                  {click?.device}
                </span>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div>
                  <span className="text-xs sm:text-sm font-medium text-tertiary">
                    IP Address :
                  </span>
                  <span className=" ml-2 text-gray-500 font-medium">
                    {click?.ip_address}
                  </span>
                </div>
                <div>
                  <span className="text-xs sm:text-sm font-medium text-tertiary">
                    Country :
                  </span>
                  <span className="ml-2 text-gray-500 font-medium">
                    {click?.country}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PromotionPerformanceView;
